import { useMemo, useState } from "react";
import {
    Heart, Scale, Zap, Dumbbell, Droplet, Bone, Eye,
    AlertTriangle, ArrowUp, ArrowDown, Check, ChevronDown, ChevronRight, Sparkles, Download,
} from "lucide-react";
import { VITAL_SPECS, CATEGORY_ORDER, CATEGORY_LABELS, statusOf, formatRange } from "../lib/vitalSpecs";
import { exportKioskReportPdf } from "../lib/pdfExport";
import { toast } from "sonner";

const CATEGORY_ICON = {
    cardio: Heart,
    weight: Scale,
    fat: Zap,
    muscle: Dumbbell,
    fluid: Droplet,
    bone: Bone,
    sensory: Eye,
};

const STATUS_STYLE = {
    high: { text: "text-red-600", bg: "bg-red-50", border: "border-red-200", pillBg: "bg-red-100", pillText: "text-red-700", label: "HIGH" },
    low: { text: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", pillBg: "bg-blue-100", pillText: "text-blue-700", label: "LOW" },
    normal: { text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-100", pillBg: "bg-emerald-100", pillText: "text-emerald-700", label: "NORMAL" },
    unknown: { text: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200", pillBg: "bg-gray-100", pillText: "text-gray-600", label: "—" },
};

const FLAG_STYLE = {
    urgent_leave: { bg: "from-red-500 to-red-600", ring: "ring-red-200", label: "URGENT LEAVE", icon: AlertTriangle, help: "BP ≥ 140/92 or HR > 100. Please stop working, rest, and contact your treating doctor." },
    take_a_break: { bg: "from-amber-500 to-orange-500", ring: "ring-amber-200", label: "TAKE A BREAK", icon: AlertTriangle, help: "BP or HR is creeping up. Book a short mandatory break within the next hour." },
    ok: { bg: "from-emerald-500 to-emerald-600", ring: "ring-emerald-200", label: "OPTIMAL", icon: Check, help: "Cardio vitals are inside the healthy operating range. Keep it up." },
};

function StatusPill({ status }) {
    const s = STATUS_STYLE[status];
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono tracking-wider ${s.pillBg} ${s.pillText}`}>
            {status === "high" ? <ArrowUp className="w-3 h-3" /> : status === "low" ? <ArrowDown className="w-3 h-3" /> : status === "normal" ? <Check className="w-3 h-3" /> : null}
            {s.label}
        </span>
    );
}

function MetricRow({ spec, value }) {
    const status = statusOf(spec.key, value);
    const style = STATUS_STYLE[status];
    const display = value === null || value === undefined || value === "" ? "—" : (typeof value === "number" ? (Number.isInteger(value) ? value : Number(value).toFixed(1)) : String(value));
    return (
        <div className={`flex items-center justify-between gap-3 border-l-2 pl-3 py-1.5 ${status === "unknown" ? "border-gray-100" : status === "normal" ? "border-emerald-200" : status === "high" ? "border-red-300" : "border-blue-300"}`} data-testid={`vital-row-${spec.key}`}>
            <div className="min-w-0">
                <div className="text-sm text-gray-800 truncate">{spec.label}</div>
                <div className="text-[11px] text-gray-400 font-mono">Normal: {formatRange(spec)}{spec.unit ? ` ${spec.unit}` : ""}</div>
            </div>
            <div className="text-right shrink-0">
                <div className={`font-mono text-lg font-semibold ${style.text}`}>
                    {display}{spec.unit && display !== "—" ? <span className="text-xs text-gray-400 ml-1">{spec.unit}</span> : null}
                </div>
                <StatusPill status={status} />
            </div>
        </div>
    );
}

function CategoryCard({ category, vitals, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    const Icon = CATEGORY_ICON[category];
    const specs = VITAL_SPECS.filter((s) => s.category === category);
    const abnormal = specs.filter((s) => ["high", "low"].includes(statusOf(s.key, vitals[s.key])));
    return (
        <div className="card-soft overflow-hidden" data-testid={`vitals-category-${category}`}>
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between p-4 hover:bg-orange-50/40 transition-colors"
                data-testid={`vitals-category-toggle-${category}`}
            >
                <div className="flex items-center gap-3 text-left">
                    <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${abnormal.length > 0 ? "bg-red-100 text-red-600" : "bg-orange-50 text-[var(--medx-red)]"}`}>
                        <Icon className="w-4 h-4" />
                    </div>
                    <div>
                        <div className="font-display text-base font-semibold">{CATEGORY_LABELS[category]}</div>
                        <div className="text-xs text-gray-500">
                            {abnormal.length > 0 ? (
                                <span className="text-red-600">{abnormal.length} flagged · {specs.length - abnormal.length} normal</span>
                            ) : (
                                <span className="text-emerald-600">All normal · {specs.length} metrics</span>
                            )}
                        </div>
                    </div>
                </div>
                {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            {open && (
                <div className="border-t border-orange-100 divide-y divide-gray-50 px-4 py-2">
                    {specs.map((spec) => (
                        <MetricRow key={spec.key} spec={spec} value={vitals[spec.key]} />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Kiosk executive-summary card + collapsible category cards.
 * Renders every metric captured from the kiosk with normal-range guidance and status colour.
 */
export function KioskVitalsReport({ vitals, onRunAiReport, aiReportBusy, patient }) {
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const flags = useMemo(() => {
        return VITAL_SPECS.map((s) => ({ spec: s, status: statusOf(s.key, vitals[s.key]) }))
            .filter((f) => f.status === "high" || f.status === "low");
    }, [vitals]);

    const hrFlag = vitals.hr_flag || { code: "ok" };
    const flagStyle = FLAG_STYLE[hrFlag.code] || FLAG_STYLE.ok;
    const FlagIcon = flagStyle.icon;

    const handleDownloadPdf = async () => {
        try {
            setDownloadingPdf(true);
            await exportKioskReportPdf(vitals, patient || {});
            toast.success("Report downloaded.");
        } catch (e) {
            toast.error("Could not generate PDF: " + (e?.message || "unknown"));
        } finally {
            setDownloadingPdf(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="kiosk-vitals-report">
            {/* HR / Triage flag banner */}
            <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${flagStyle.bg} text-white p-6 md:p-8 ring-4 ${flagStyle.ring}`} data-testid={`hr-flag-${hrFlag.code}`}>
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur grid place-items-center shrink-0">
                        <FlagIcon className="w-7 h-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-mono tracking-[0.22em] opacity-90">TRIAGE STATUS · KIOSK SCAN</div>
                        <div className="font-display text-2xl md:text-3xl font-bold">{hrFlag.label || flagStyle.label}</div>
                        <div className="text-sm text-white/90 mt-1 max-w-2xl">{hrFlag.message || flagStyle.help}</div>
                    </div>
                    {onRunAiReport && (
                        <button
                            onClick={onRunAiReport}
                            disabled={aiReportBusy}
                            className="hidden sm:inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 transition-colors"
                            data-testid="hr-flag-ai-report"
                        >
                            <Sparkles className="w-4 h-4" /> {aiReportBusy ? "…" : "AI Report"}
                        </button>
                    )}
                    <button
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                        className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 transition-colors"
                        data-testid="hr-flag-download-pdf"
                        title="Download this scan as a PDF"
                    >
                        <Download className="w-4 h-4" /> {downloadingPdf ? "…" : "Download PDF"}
                    </button>
                </div>
            </div>

            {/* Executive summary */}
            <div className="card-soft p-6" data-testid="vitals-executive-summary">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-1">EXECUTIVE SUMMARY</div>
                        <h3 className="font-display text-2xl font-bold">
                            {flags.length === 0
                                ? "All metrics look great."
                                : flags.length === 1
                                ? "1 metric outside normal range."
                                : `${flags.length} metrics outside normal range.`}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1" data-testid="latest-scan-meta">
                            {(() => {
                                const src = vitals._source;
                                const label = src === "upload" ? "UPLOADED" : src === "push" ? "KIOSK API" : src === "simulate" ? "SIMULATION" : null;
                                const cls = src === "upload" ? "bg-blue-100 text-blue-700" : src === "push" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600";
                                return label ? <span className={`inline-flex items-center text-[10px] font-mono tracking-wider px-2 py-0.5 rounded-full ${cls} mr-2`} data-testid="latest-scan-source">{label}</span> : null;
                            })()}
                            Kiosk {vitals.kiosk_id || "—"} · captured {vitals.captured_at ? new Date(vitals.captured_at).toLocaleString() : "—"} · Health Score <span className="font-bold text-[var(--medx-red)]">{vitals.health_score ?? "—"}</span>/100
                        </p>
                    </div>
                </div>

                {flags.length > 0 && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {flags.slice(0, 6).map((f) => {
                            const st = STATUS_STYLE[f.status];
                            return (
                                <div key={f.spec.key} className={`rounded-xl p-3 border ${st.border} ${st.bg}`} data-testid={`vitals-flag-${f.spec.key}`}>
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <div className={`text-xs font-mono ${st.text}`}>{f.spec.label}</div>
                                        <StatusPill status={f.status} />
                                    </div>
                                    <div className={`font-mono text-lg font-bold ${st.text}`}>
                                        {vitals[f.spec.key]}<span className="text-xs text-gray-400 ml-1">{f.spec.unit}</span>
                                    </div>
                                    <div className="text-[11px] text-gray-500 mt-0.5">Target: {formatRange(f.spec)} {f.spec.unit}</div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 7 category cards */}
            <div className="space-y-3">
                {CATEGORY_ORDER.map((cat) => (
                    <CategoryCard key={cat} category={cat} vitals={vitals} defaultOpen={cat === "cardio"} />
                ))}
            </div>
        </div>
    );
}
